/**
 * Picker — Variation A (editorial 6-card grid overlay).
 *
 * Open animation (Animation Spec §4.1):
 *   - Backdrop fades in over 240ms
 *   - Cards stagger by 80ms each (360ms duration each)
 *   - Header lands last (after the cards)
 * Close (§4.2): single 200ms fade. No reverse stagger.
 *
 * Accessibility:
 *   - Focus trapped inside the overlay
 *   - Esc closes and returns focus to the trigger
 *   - Body scroll locked while open
 *   - Coming-soon cards are non-interactive but visible
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { FocusTrap } from '../primitives/FocusTrap';
import { Cover } from './Cover';
import { ALBUM_COUNT, LIBRARY } from '@/data/albums';
import { useTrack } from '@/analytics';
import type { AlbumLibraryEntry } from '@/types/album';

interface PickerProps {
  open: boolean;
  currentId: string;
  onClose: () => void;
  onPick: (id: string) => void;
  /** Trigger element to return focus to on close. */
  triggerRef: React.RefObject<HTMLButtonElement | null>;
}

export function Picker({
  open,
  currentId,
  onClose,
  onPick,
  triggerRef,
}: PickerProps): JSX.Element | null {
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const onCloseRef = useRef(onClose);
  const onPickRef = useRef(onPick);
  const track = useTrack();

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    onPickRef.current = onPick;
  }, [onPick]);

  const startClose = useCallback(() => {
    if (closeTimer.current) return;
    setClosing(true);
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null;
      setClosing(false);
      onCloseRef.current();
    }, 200);
  }, []);

  // Esc to close, body scroll lock while open.
  useEffect(() => {
    if (!open) {
      if (closeTimer.current) {
        window.clearTimeout(closeTimer.current);
        closeTimer.current = null;
      }
      setClosing(false);
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        startClose();
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, startClose]);

  useEffect(() => {
    return () => {
      if (closeTimer.current) {
        window.clearTimeout(closeTimer.current);
        closeTimer.current = null;
      }
    };
  }, []);

  const handlePick = useCallback(
    (lib: AlbumLibraryEntry) => {
      if (!lib.available) return;
      if (lib.id !== currentId) {
        track('Album Switch', { from: currentId, to: lib.id });
        onPickRef.current(lib.id);
      }
      startClose();
    },
    [currentId, startClose, track]
  );

  if (!open) return null;

  return (
    <FocusTrap active={open} returnFocus={() => triggerRef.current}>
      <div
        className={`ap-overlay ${closing ? 'is-closing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ap-overlay-title"
      >
        {/*
          Backdrop is a real button so keyboard users can dismiss the picker
          via Tab cycle + Enter, in addition to the Esc keydown handler above
          and the explicit close button in the header.
        */}
        <button
          type="button"
          className="ap-overlay-backdrop"
          aria-label="Cerrar el selector"
          onClick={startClose}
          tabIndex={-1}
        />
        <header className="ap-overlay-head">
          <div className="ap-overlay-eyebrow">Biblioteca · {String(ALBUM_COUNT).padStart(2, '0')} discos</div>
          <h2 className="ap-overlay-title" id="ap-overlay-title">
            Elegí un disco para entrar.
          </h2>
          <button
            type="button"
            className="ap-overlay-close"
            onClick={startClose}
            aria-label="Cerrar el selector"
          >
            ✕
          </button>
        </header>

        <div className="ap-grid">
          {LIBRARY.map((lib, idx) => {
            const isCurrent = lib.id === currentId;
            const isUnavailable = !lib.available;
            const cls = [
              'ap-card',
              isCurrent ? 'is-current' : '',
              isUnavailable ? 'is-unavailable' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <button
                key={lib.id}
                type="button"
                className={cls}
                style={{ animationDelay: `${idx * 80}ms` }}
                onClick={() => handlePick(lib)}
                disabled={isUnavailable}
                aria-current={isCurrent ? 'true' : undefined}
                aria-label={`${lib.album} de ${lib.artist}${isCurrent ? ' (actual)' : ''}${isUnavailable ? ' (próximamente)' : ''}`}
              >
                <span className="ap-card-num">{String(idx + 1).padStart(2, '0')}</span>
                <span className="ap-card-cover-wrap">
                  <Cover album={lib} size={252} />
                  <span className="ap-card-reveal">
                    <span className="ap-reveal-cta">
                      {isUnavailable ? 'Próximamente' : isCurrent ? 'Actual' : 'Entrar al disco'}
                    </span>
                  </span>
                </span>
                <span className="ap-card-base">
                  <span>
                    <span className="ap-card-album">{lib.album}</span>
                    <span className="ap-card-artist">{lib.artist}</span>
                  </span>
                  <span className="ap-card-status">
                    {isCurrent ? 'Actual' : isUnavailable ? 'Próximamente' : 'Disponible'}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="ap-overlay-foot">
          Cada disco se lee como un sistema. Si querés sugerir uno, escribime.
        </div>
      </div>
    </FocusTrap>
  );
}
